import { useMutation } from '@tanstack/react-query'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Select from 'components/Common/Form/Input/Select'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { rem } from 'polished'
import type { FormEventHandler } from 'react'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import requestPostCreateUserWithSociete from 'agentHub/api/requestPostCreateUserWithSociete'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX } from 'constants/config/region'
import config from 'constants/config'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { SET_SOCIETE_REQUESTS_INTERACTIONS } from 'actions/actionConstants'
import getAgentHubPhonePlaceholder from 'agentHub/utils/getAgentHubPhonePlaceholder'

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "firstName lastName"
                         "email email"
                         "password password"
                         "regionCode regionCode"
                         "phone phone";
    .grid-firstName {
      grid-area: firstName;
    }
    .grid-lastName {
      grid-area: lastName;
    }
    .grid-email {
      grid-area: email;
    }
    .grid-password {
      grid-area: password;
    }
    .grid-regionCode {
      grid-area: regionCode;
    }
    .grid-phone {
      grid-area: phone;
    }
  }
`

interface CreateFormData {
  firstName: string
  lastName: string
  email: string
  password: string
  regionCode: 'AU' | 'NZ' | 'US' | 'GB'
  phone?: { phone: string; prefix: string }
}

interface Props {
  onSuccess: () => void
  onEmailSent: () => void
  defaultEmail?: string
}

function SocieteCreateNewForm({ onSuccess, onEmailSent, defaultEmail }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const geoRegion = useAppSelector(state => state.geo.currentRegionCode)
  const [regionCode, setRegionCode] = useState<'AU' | 'NZ' | 'US' | 'GB'>(geoRegion as 'AU' | 'NZ' | 'US' | 'GB')
  const [isError, setIsError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const brand = agentHubOrSubBrandConfig.isCVFREnabled ? 'leagenthub_cvfr' : 'leagenthub'

  const createMutation = useMutation({
    mutationFn: async(formData: CreateFormData) => {
      return requestPostCreateUserWithSociete({
        email: formData.email,
        password: formData.password,
        givenName: formData.firstName,
        surname: formData.lastName,
        region: formData.regionCode,
        phonePrefix: formData.phone?.prefix,
        phone: formData.phone?.phone,
        brand,
      })
    },
    onSuccess: (result) => {
      setIsError(false)
      dispatch({
        type: SET_SOCIETE_REQUESTS_INTERACTIONS,
        interactions: { engaged: { engagedAt: new Date().toISOString() } },
      })
      if (result.confirmationRequired) {
        onEmailSent()
      } else {
        onSuccess()
      }
    },
    onError: (error: App.ApiCallFailure) => {
      setErrorMsg(error.message)
      setIsError(true)
      setIsSubmitted(false)
    },
  })

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsSubmitted(true)
    setIsError(false)
    const formData = formToObject<CreateFormData>(e.currentTarget)
    createMutation.mutate({
      ...formData,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
    })
  }, [createMutation])

  const nameInvalidErrorMessage = intl.formatMessage({
    id: '_c09YlOlkCh2PAtzyo5m',
    defaultMessage: 'Invalid characters. Please use only letters and spaces',
  })

  return (
    <Group direction="vertical" gap={20}>
      {isError && (
        <MessageBanner kind="critical">
          {intl.formatMessage({
            id: 'PwmevK-wFcKK-NGMHaaJ',
            defaultMessage: 'Something went wrong while creating your account. Error: {error}',
          }, { error: errorMsg })}
        </MessageBanner>
      )}
      <BodyText variant="medium">
        {intl.formatMessage({
          id: 'FXH3i2qj9sCMiOE_8wOe',
          defaultMessage: 'Create a new Luxury Escapes account with Société membership to start earning loyalty points.',
        })}
      </BodyText>

      <form onSubmit={handleSubmit} name="societeCreateNewForm">
        <Group direction="vertical" gap={16}>
          <InputGrid>
            <TextInput
              className="grid-firstName"
              invalidErrorMessage={nameInvalidErrorMessage}
              label={intl.formatMessage({
                id: 'a6N52vR0bv1c8qSD0F6Q',
                defaultMessage: 'First name',
              })}
              maxLength={100}
              minLength={2}
              name="firstName"
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage({
                id: 'jYvRs8kQjBfrlyVuSFd1',
                defaultMessage: 'First name',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: '283tPBm2q3imViCI54dl',
                defaultMessage: 'First name is required',
              })}
            />
            <TextInput
              className="grid-lastName"
              invalidErrorMessage={nameInvalidErrorMessage}
              label={intl.formatMessage({
                id: '9EGkVf1Bm6XlFq7T9FCb',
                defaultMessage: 'Last name',
              })}
              maxLength={100}
              minLength={2}
              name="lastName"
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage({
                id: 'sVj5dqbwfMkKRELvj6Po',
                defaultMessage: 'Last name',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'KA_JX5eIFWmKYGrrIRaw',
                defaultMessage: 'Last name is required',
              })}
            />
            <TextInput
              className="grid-email"
              defaultValue={defaultEmail}
              invalidErrorMessage={intl.formatMessage({
                id: 'bDbFq6fD7PZBRyNoARtO',
                defaultMessage: 'Invalid email address',
              })}
              label={intl.formatMessage({
                id: 'tXAVb1qVErwt7MVfZjUb',
                defaultMessage: 'Email address',
              })}
              maxLength={100}
              name="email"
              placeholder={intl.formatMessage({
                id: 'uj0YPpzXRlEcbA7ClQd0',
                defaultMessage: 'Enter your email address',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'Jp-f7TDsMZYWZ2xRQSwh',
                defaultMessage: 'Email address is required',
              })}
              type="email"
            />
            <PasswordInput
              className="grid-password"
              label={intl.formatMessage({
                id: 'BDb6zVbslQFizrnB_5rP',
                defaultMessage: 'Password',
              })}
              minLength={8}
              name="password"
              placeholder={intl.formatMessage({
                id: 'K-Oqh20XmoKpRG6yqhCw',
                defaultMessage: 'Create a password',
              })}
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'ZJKeAs3CI0EfGqlLvdfg',
                defaultMessage: 'Password is required',
              })}
            />
            <Select
              className="grid-regionCode"
              label={intl.formatMessage({
                id: '7qrZwUuhezumTiEPDaID',
                defaultMessage: 'Region',
              })}
              name="regionCode"
              required
              requiredErrorMessage={intl.formatMessage({
                id: 'NduAmoqxZ4azf8A1ETaM',
                defaultMessage: 'Region is required',
              })}
              value={regionCode}
              onChange={(e) => setRegionCode(e.target.value as 'AU' | 'NZ' | 'US' | 'GB')}
            >
              {config.regions.map((item) => item.name && <option key={item.code} value={item.code}>{item.name}</option>)}
            </Select>
            <PhoneInput
              allowLandline
              className="grid-phone"
              prefixCountry={regionCode}
              defaultPhonePrefix={AGENT_HUB_DEFAULT_PHONE_NUMBER_PREFIX[regionCode]}
              label={intl.formatMessage({
                id: 'NBPJD5UziOwYf-ywXwTw',
                defaultMessage: 'Phone number (optional)',
              })}
              name="phone"
              key={regionCode}
              placeholder={getAgentHubPhonePlaceholder(intl, regionCode)}
            />
          </InputGrid>
          <TextButton disabled={isSubmitted} fit="mobile-full-width" kind="primary" size="large" type="submit">
            {intl.formatMessage({
              id: 'feilofU4Ju-Oa8dCinfR',
              defaultMessage: 'Create account',
            })}
          </TextButton>
        </Group>
      </form>
    </Group>
  )
}

export default SocieteCreateNewForm
